/**
 * Auth Form Components
 *
 * Reusable form components for authentication.
 * Styled with CSS variables for dark mode support.
 */

import React from 'react';

// ============================================================
// Input Component
// ============================================================

interface InputProps {
  type: string;
  name: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  minLength?: number;
  autoComplete?: string;
}

export function Input({
  type,
  name,
  placeholder,
  value,
  onChange,
  required = false,
  minLength,
  autoComplete,
}: InputProps) {
  return (
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      minLength={minLength}
      autoComplete={autoComplete}
      style={{
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid var(--border-color, #e5e7eb)',
        borderRadius: '8px',
        backgroundColor: 'var(--input-bg, #ffffff)',
        color: 'var(--text-color, #1f2937)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    />
  );
}

// ============================================================
// Button Component
// ============================================================

interface ButtonProps {
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'google';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

export function Button({
  type = 'button',
  variant = 'primary',
  disabled = false,
  loading = false,
  onClick,
  children,
}: ButtonProps) {
  const baseStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    borderRadius: '8px',
    border: 'none',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.7 : 1,
    transition: 'background-color 0.2s, opacity 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const variants: Record<string, React.CSSProperties> = {
    primary: {
      ...baseStyle,
      backgroundColor: 'var(--primary-color, #2563eb)',
      color: '#ffffff',
    },
    secondary: {
      ...baseStyle,
      backgroundColor: 'transparent',
      color: 'var(--text-color, #1f2937)',
      border: '1px solid var(--border-color, #e5e7eb)',
    },
    google: {
      ...baseStyle,
      backgroundColor: '#ffffff',
      color: '#1f2937',
      border: '1px solid #e5e7eb',
    },
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      style={variants[variant]}
    >
      {loading ? 'Loading...' : children}
    </button>
  );
}

// ============================================================
// Google Icon
// ============================================================

export function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

// ============================================================
// Divider
// ============================================================

export function Divider({ text }: { text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        margin: '24px 0',
      }}
    >
      <div
        style={{
          flex: 1,
          height: '1px',
          backgroundColor: 'var(--border-color, #e5e7eb)',
        }}
      />
      <span
        style={{
          color: 'var(--text-muted, #6b7280)',
          fontSize: '14px',
        }}
      >
        {text}
      </span>
      <div
        style={{
          flex: 1,
          height: '1px',
          backgroundColor: 'var(--border-color, #e5e7eb)',
        }}
      />
    </div>
  );
}

// ============================================================
// Error Message
// ============================================================

export function ErrorMessage({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: 'var(--color-error-soft)',
        border: '1px solid var(--color-error-border)',
        borderRadius: '8px',
        color: 'var(--color-error)',
        fontSize: '14px',
      }}
    >
      {message}
    </div>
  );
}

// ============================================================
// Success Message
// ============================================================

export function SuccessMessage({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: 'var(--color-success-soft)',
        border: '1px solid var(--color-success-border)',
        borderRadius: '8px',
        color: 'var(--color-success)',
        fontSize: '14px',
      }}
    >
      {message}
    </div>
  );
}
